/* CANFOTO ETAP 2 — R8-B COLLECTION CANONICAL RANKING TRUTH ADAPTER
   Read-only. No DOM reorder. No scoring. No cross-engine normalization. */
(()=>{
'use strict';

const VERSION='CANFOTO-ETAP2-R8-B-COLLECTION-CANONICAL-RANKING-ADAPTER-V1';
const PANEL_VERSION='CANFOTO-ETAP2-R8-B-PANEL-FROZEN-COLLECTION-RANKING-V1';
const COB_VERSION='CANFOTO-ETAP2-R8-B-COB-FROZEN-COLLECTION-RANKING-V1';
const R7_VERSION='CANFOTO-ETAP2-R7-COLLECTION-WHOLE-POOL-READ-MODEL-V1';

const EXPECT=Object.freeze({
  panel:{
    matrix:'6a20ce23e7b33e280e263f2bdea5e5799c920ae81c77069ae00ab14ba6059e62',
    fp:'87585548527b2ca531ff559acee73475094fcd2e1e35796fea1e44b98812b32e',
    order:'c9f418c81589376629b5740962e5d151ee6daeedfe44e17f381c657eb0432f67',
    fpOrder:'d7d96a943a85a1771b96909166184cb0bd31a0a30f75faefb2e6d60a35d15603'
  },
  cob:{
    catalog:'sha256:da4500806344471a2d67e4682f689810494b34ccb66da54fbc86ac223346283b',
    matrix:'46400106f901486fc9ce69f2f45e2239cfe9a51080638c4577a6b3985e100501',
    fp:'a616d42613e7a7081293635efc10288dc184d8eb6db44c3f38adfbdc59d583c2',
    order:'295a8a9a42a77481cfb388f29daa69990a8e7ec93e6920c3253f5333e3096ee8',
    fpOrder:'5cfc9798455c3e41057edf7bd74220eb358f945add0afde399f510b26bba057f'
  }
});

const clean=v=>String(v??'').replace(/\s+/g,' ').trim();
const freeze=v=>{
  if(!v||typeof v!=='object'||Object.isFrozen(v)) return v;
  Object.keys(v).forEach(k=>freeze(v[k]));
  return Object.freeze(v);
};
const sleep=ms=>new Promise(r=>setTimeout(r,ms));
const sequential=rows=>rows.every((r,i)=>Number(r.rank)===i+1);

const state={
  status:'BOOTING',
  error:'',
  version:VERSION,
  sourceMode:'FROZEN_CANONICAL_ENGINE_RANK_ORDER',
  panelReady:false,
  cobReady:false,
  readModelReady:false,
  crossEngineNormalization:'FORBIDDEN',
  stockRankingInfluence:'NONE',
  scoringMutation:'NONE',
  rankingComputationMutation:'NONE',
  fpMutation:'NONE',
  setupTruthMutation:'NONE',
  commerceMutation:'NONE',
  domMutation:'NONE',
  sortMutation:'NONE',
  filterMutation:'NONE',
  urlMutation:'NONE'
};

let panel=null;
let cob=null;
let r7=null;
let panelSelectionMap=null;
let cobContextMap=null;
let panelFpMap=null;
let cobFpMap=null;

function exactSet(a,b){
  if(a.length!==b.length)return false;
  const A=new Set(a),B=new Set(b);
  return A.size===B.size&&[...A].every(x=>B.has(x));
}
function indexHandles(engineOwner){
  return (r7?.getByEngine?.(engineOwner)||[]).map(x=>clean(x.productHandle)).filter(Boolean);
}
function validatePanel(){
  const s=window.CANFOTO_R8_PANEL_RANKING_SNAPSHOT;
  if(!s||clean(s.version)!==PANEL_VERSION) throw new Error('R8B_PANEL_SNAPSHOT_VERSION_MISMATCH');
  const d=s.data;
  if(d?.source?.matrixFingerprint!==EXPECT.panel.matrix) throw new Error('R8B_PANEL_MATRIX_FP_MISMATCH');
  if(d?.source?.fpFingerprint!==EXPECT.panel.fp) throw new Error('R8B_PANEL_FP_FP_MISMATCH');
  if(d?.source?.rankingOrderFingerprint!==EXPECT.panel.order) throw new Error('R8B_PANEL_ORDER_FP_MISMATCH');
  if(d?.source?.fpOrderFingerprint!==EXPECT.panel.fpOrder) throw new Error('R8B_PANEL_FP_ORDER_FP_MISMATCH');
  if(Number(d?.counts?.products)!==27||Number(d?.counts?.selections)!==25) throw new Error('R8B_PANEL_COUNT_MISMATCH');
  if(!Array.isArray(d.generalFpOrder)||d.generalFpOrder.length!==27||!sequential(d.generalFpOrder)) throw new Error('R8B_PANEL_FP_ORDER_INVALID');
  if(!Array.isArray(d.selections)||d.selections.length!==25) throw new Error('R8B_PANEL_SELECTIONS_INVALID');

  const sourceHandles=d.generalFpOrder.map(x=>clean(x.productHandle));
  const r7Handles=indexHandles('GDX_PANEL_LED_V4');
  if(!exactSet(sourceHandles,r7Handles)) throw new Error('R8B_PANEL_R7_HANDLE_PARITY_FAIL');

  for(const sel of d.selections){
    for(const lane of ['ALL','BARE_LIGHT','SINGLE_READY_SET']){
      const rows=sel?.orders?.[lane];
      if(!Array.isArray(rows)||!sequential(rows)) throw new Error('R8B_PANEL_ORDER_INVALID:'+clean(sel.selectionKey)+':'+lane);
    }
    if(sel.orders.ALL.length!==27) throw new Error('R8B_PANEL_ALL_COUNT_INVALID:'+clean(sel.selectionKey));
    if(sel.orders.BARE_LIGHT.length+sel.orders.SINGLE_READY_SET.length!==27)
      throw new Error('R8B_PANEL_LANE_PARTITION_INVALID:'+clean(sel.selectionKey));
    if(!exactSet(sel.orders.ALL.map(x=>clean(x.productHandle)),sourceHandles))
      throw new Error('R8B_PANEL_ALL_HANDLE_SET_INVALID:'+clean(sel.selectionKey));
  }

  panel=s;
  panelSelectionMap=new Map(d.selections.map(x=>[clean(x.selectionKey),x]));
  panelFpMap=new Map(d.generalFpOrder.map(x=>[clean(x.productHandle),x]));
  state.panelReady=true;
}
function validateCob(){
  const s=window.CANFOTO_R8_COB_RANKING_SNAPSHOT;
  if(!s||clean(s.version)!==COB_VERSION) throw new Error('R8B_COB_SNAPSHOT_VERSION_MISMATCH');
  const d=s.data;
  if(d?.source?.catalogFingerprint!==EXPECT.cob.catalog) throw new Error('R8B_COB_CATALOG_FP_MISMATCH');
  if(d?.source?.matrixFingerprint!==EXPECT.cob.matrix) throw new Error('R8B_COB_MATRIX_FP_MISMATCH');
  if(d?.source?.fpFingerprint!==EXPECT.cob.fp) throw new Error('R8B_COB_FP_FP_MISMATCH');
  if(d?.source?.rankingOrderFingerprint!==EXPECT.cob.order) throw new Error('R8B_COB_ORDER_FP_MISMATCH');
  if(d?.source?.fpOrderFingerprint!==EXPECT.cob.fpOrder) throw new Error('R8B_COB_FP_ORDER_FP_MISMATCH');
  if(Number(d?.counts?.products)!==36||Number(d?.counts?.purposes)!==36||Number(d?.counts?.contexts)!==72)
    throw new Error('R8B_COB_COUNT_MISMATCH');
  if(!Array.isArray(d.generalFpOrder)||d.generalFpOrder.length!==36||!sequential(d.generalFpOrder))
    throw new Error('R8B_COB_FP_ORDER_INVALID');
  if(!Array.isArray(d.purposeDefs)||d.purposeDefs.length!==36) throw new Error('R8B_COB_PURPOSE_DEFS_INVALID');
  if(!Array.isArray(d.contexts)||d.contexts.length!==72) throw new Error('R8B_COB_CONTEXTS_INVALID');

  const sourceHandles=d.generalFpOrder.map(x=>clean(x.productHandle));
  const sourceSet=new Set(sourceHandles);
  const r7Handles=indexHandles('GDX_COB_LED_V5');
  if(!exactSet(sourceHandles,r7Handles)) throw new Error('R8B_COB_R7_HANDLE_PARITY_FAIL');

  for(const ctx of d.contexts){
    if(!['BARE_LIGHT','SINGLE_READY_SET'].includes(clean(ctx.lane)))
      throw new Error('R8B_COB_LANE_INVALID:'+clean(ctx.purposeKey));
    if(!Array.isArray(ctx.ordered)||!sequential(ctx.ordered))
      throw new Error('R8B_COB_ORDER_INVALID:'+clean(ctx.purposeKey)+':'+clean(ctx.lane));
    if(ctx.ordered.some(x=>!sourceSet.has(clean(x.productHandle))))
      throw new Error('R8B_COB_NONCANONICAL_HANDLE:'+clean(ctx.purposeKey)+':'+clean(ctx.lane));
    if(ctx.ordered.some(x=>clean(x.lane)!==clean(ctx.lane)))
      throw new Error('R8B_COB_CONTEXT_LANE_MISMATCH:'+clean(ctx.purposeKey)+':'+clean(ctx.lane));
  }

  cob=s;
  cobContextMap=new Map(d.contexts.map(x=>[clean(x.purposeKey)+'::'+clean(x.lane),x]));
  cobFpMap=new Map(d.generalFpOrder.map(x=>[clean(x.productHandle),x]));
  state.cobReady=true;
}

function handlesFilter(rows,handles){
  if(!Array.isArray(handles)||!handles.length)return rows.slice();
  const allow=new Set(handles.map(clean).filter(Boolean));
  return rows.filter(r=>allow.has(clean(r.productHandle)));
}
function laneFilter(rows,lane){
  const l=clean(lane);
  if(!l||l==='ALL')return rows.slice();
  return rows.filter(r=>clean(r.lane)===l);
}
function getGeneralFpOrder(engine,options={}){
  const e=clean(engine).toUpperCase();
  const rows=e==='PANEL'?panel.data.generalFpOrder:e==='COB'?cob.data.generalFpOrder:null;
  if(!rows) throw new Error('R8B_ENGINE_REQUIRED_PANEL_OR_COB');
  return freeze(handlesFilter(laneFilter(rows,options.lane),options.handles));
}
function getPurposeOrder(engine,key,options={}){
  const e=clean(engine).toUpperCase();
  const k=clean(key);
  if(!k) throw new Error('R8B_PURPOSE_OR_SELECTION_KEY_REQUIRED');

  if(e==='PANEL'){
    const sel=panelSelectionMap.get(k);
    if(!sel) return freeze([]);
    const lane=clean(options.lane||'ALL');
    if(!['ALL','BARE_LIGHT','SINGLE_READY_SET'].includes(lane))
      throw new Error('R8B_PANEL_LANE_INVALID');
    return freeze(handlesFilter(sel.orders[lane],options.handles));
  }

  if(e==='COB'){
    const lane=clean(options.lane);
    if(!['BARE_LIGHT','SINGLE_READY_SET'].includes(lane))
      throw new Error('R8B_COB_PURPOSE_ORDER_REQUIRES_PURCHASE_LANE');
    const ctx=cobContextMap.get(k+'::'+lane);
    if(!ctx) return freeze([]);
    return freeze(handlesFilter(ctx.ordered,options.handles));
  }

  throw new Error('R8B_ENGINE_REQUIRED_PANEL_OR_COB');
}
function getPurposeDefinition(engine,key){
  const e=clean(engine).toUpperCase(),k=clean(key);
  if(e==='PANEL'){
    const s=panelSelectionMap.get(k);
    return s?freeze({
      selectionKey:s.selectionKey,purposeKey:s.purposeKey,subPurposeKey:s.subPurposeKey,
      purposeTitle:s.purposeTitle,subPurposeTitle:s.subPurposeTitle,
      intent:s.intent,priorityRole:s.priorityRole
    }):null;
  }
  if(e==='COB'){
    return freeze(cob.data.purposeDefs.find(x=>clean(x.purposeKey)===k)||null);
  }
  return null;
}
function getGeneralFp(engine,handle){
  const e=clean(engine).toUpperCase(),h=clean(handle);
  const row=e==='PANEL'?panelFpMap.get(h):e==='COB'?cobFpMap.get(h):null;
  return row?freeze(row):null;
}
function getProductPurposeRank(engine,key,handle,options={}){
  const h=clean(handle);
  return getPurposeOrder(engine,key,options).find(x=>clean(x.productHandle)===h)||null;
}
function getCurrentPagePurposeOrder(engine,key,options={}){
  const page=r7.getCurrentPage?.();
  return getPurposeOrder(engine,key,{...options,handles:page?.canonicalHandles||[]});
}
function getCurrentPageFpOrder(engine,options={}){
  const page=r7.getCurrentPage?.();
  return getGeneralFpOrder(engine,{...options,handles:page?.canonicalHandles||[]});
}
function getState(){
  return freeze({...state,
    panelSource:panel?.data?.source||null,
    cobSource:cob?.data?.source||null,
    panelCounts:panel?.data?.counts||null,
    cobCounts:cob?.data?.counts||null
  });
}

const API=Object.freeze({
  version:VERSION,
  sourceMode:'FROZEN_CANONICAL_ENGINE_RANK_ORDER',
  crossEngineNormalization:'FORBIDDEN',
  stockRankingInfluence:'NONE',
  getState,
  getGeneralFpOrder,
  getPurposeOrder,
  getPurposeDefinition,
  getGeneralFp,
  getProductPurposeRank,
  getCurrentPagePurposeOrder,
  getCurrentPageFpOrder
});

async function boot(){
  try{
    const started=performance.now();
    while(performance.now()-started<20000){
      r7=window.CANFOTO_R7_COLLECTION_READ_MODEL||null;
      const s=r7?.getState?.();
      if(r7&&s?.status==='READY'&&clean(s.version)===R7_VERSION)break;
      await sleep(50);
    }
    if(!r7||r7.getState?.().status!=='READY') throw new Error('R8B_R7_READ_MODEL_NOT_READY');
    state.readModelReady=true;

    validatePanel();
    validateCob();

    state.status='READY';
    window.CANFOTO_R8_COLLECTION_RANKING_ADAPTER=API;
    window.dispatchEvent(new CustomEvent('canfoto:r8-ranking-adapter-ready',{
      detail:{
        version:VERSION,
        sourceMode:API.sourceMode,
        panelCounts:panel.data.counts,
        cobCounts:cob.data.counts
      }
    }));
  }catch(error){
    state.status='ERROR_FAIL_CLOSED';
    state.error=clean(error?.message||error);
    window.CANFOTO_R8_COLLECTION_RANKING_ADAPTER=API;
    console.error('[CANFOTO R8-B Ranking Adapter]',error);
  }
}

window.CANFOTO_R8_COLLECTION_RANKING_ADAPTER=API;
if(document.readyState==='loading'){
  document.addEventListener('DOMContentLoaded',boot,{once:true});
}else{
  boot();
}
})();
